import { useMemo } from 'react';
import { useRetrieveWorkspaceBooks } from '@/generated/hooks/bdk/v2/services/library';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import { BOOK_CATALOG_STALE_TIME } from '@/modules/integrations/constants';
import { EMAIL_BOOK_NAME } from '../components/EmailTriggerDialog/constants';

/**
 * Which trigger types this workspace can offer, based on the installed books.
 *
 * This answers "should the webhook / poll options appear in the picker", not
 * "what triggers does this automation have" — it is workspace-scoped and
 * automation-independent, so callers must not block automation-scoped UI on
 * `isLoading`. Sharing one hook (and one stale time) across the picker and the
 * trigger dialogs means the two catalog requests are issued once per workspace
 * rather than once per call site.
 *
 * @returns Availability flags for webhook and poll triggers, plus a loading flag
 */
export function useTriggerCapabilities(): {
  hasWebhookTrigger: boolean;
  hasPollTrigger: boolean;
  isLoading: boolean;
} {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const enabled = Boolean(organizationId && workspaceId);

  const { data: triggerBooksData, isLoading: isLoadingTriggerBooks } =
    useRetrieveWorkspaceBooks(
      organizationId,
      workspaceId,
      {
        page_size: 100,
        filter: FilterBuilder.create().equals('has_triggers', true).build(),
      },
      { query: { enabled, staleTime: BOOK_CATALOG_STALE_TIME } },
    );

  const { data: pollBooksData, isLoading: isLoadingPollBooks } =
    useRetrieveWorkspaceBooks(
      organizationId,
      workspaceId,
      {
        page_size: 100,
        filter: FilterBuilder.create().equals('has_polls', true).build(),
      },
      { query: { enabled, staleTime: BOOK_CATALOG_STALE_TIME } },
    );

  // Email has its own dedicated trigger type, so it doesn't count towards the
  // generic webhook option.
  const hasWebhookTrigger = useMemo(
    () =>
      (triggerBooksData?.data?.books ?? []).some(
        book =>
          Boolean(book.name) &&
          book.name?.toLowerCase() !== EMAIL_BOOK_NAME.toLowerCase(),
      ),
    [triggerBooksData?.data?.books],
  );

  const hasPollTrigger = Boolean(pollBooksData?.data?.books?.length);

  return {
    hasWebhookTrigger,
    hasPollTrigger,
    isLoading: isLoadingTriggerBooks || isLoadingPollBooks,
  };
}
